import { useField } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useIntl } from "react-intl";
import { SearchImageFormContainer } from "src/app/containers";
import { UserRole } from "src/app/transports/user";
import { ImageContent } from "src/app/types/content";
import { ContentType, ISuggestedWords, Order, WritingType } from "src/app/types/orders/order";
import { IHits } from "src/app/types/pixabay";
import {
    allChecksPassed,
    getTextValidation,
} from "src/app/utils/textCheckHandler/getTextValidation";
import { handleData, TDocMetrics } from "src/app/utils/textCheckHandler/handleData";
import { CkEditorCustom, ValidationIconHandler } from "..";
import { SimpleFormControl } from "../form/SimpleFormControl";
import { SimpleFormSelect, SimpleFormSelectOption } from "../form/SimpleFormSelect";
import { SimpleFormTextarea } from "../form/SimpleFormTextarea";
import { stringsToOptions } from "src/app/utils/functionsHelpers";
import { copyLinkToClipboard } from "src/app/utils/stringHelper";
import { useAuth } from "src/app/utils/useAuth";

export interface IReviewTextOrderForm {
    entity?: Order;
    userRole?: UserRole;
    docMetrics?: TDocMetrics;
    setDocMetrics: React.Dispatch<React.SetStateAction<TDocMetrics | undefined>>;
    handleDisableButton?: (disable: boolean) => void;
    importantReason?: boolean;
    suggestedWords?: {
        suggestedWords?: ISuggestedWords;
        refresh(): void;
        loadingSuggestedWords: boolean;
    };
}
const ReviewTextOrderForm = (props: IReviewTextOrderForm) => {
    const {
        entity,
        handleDisableButton = () => {},
        docMetrics,
        setDocMetrics,
        importantReason,
    } = props;

    const { currentUser } = useAuth();

    //States
    const [headersOptions, setHeaderOptions] = useState<Array<SimpleFormSelectOption>>();

    //Fields
    const [, textMetaProps, textHelperProps] = useField(`text`);
    const [, titleMetaProps] = useField(`title`);
    const [, categoryMetaProps] = useField(`category`);
    const [, rateMetaProps] = useField(`rate`);
    const [, metaTitleMetaProps] = useField(`metaTitle`);
    const [, metaDescriptionMetaProps] = useField(`metaDescription`);
    const [, imagesMetaProps, imagesHelperProps] = useField(`images`);
    const [, commnetFromProofreaderMetaProps, commnetFromProofreaderHelperProps] =
        useField(`commentFromProofreader`);

    const { formatMessage } = useIntl();

    const inputValues = {
        metaTitle: metaTitleMetaProps.value,
        title: titleMetaProps.value,
        metaDescription: metaDescriptionMetaProps.value,
    };
    const categoriesList = entity?.rd?.categories?.map((category) => ({
        label: category || "",
        value: category || "",
    }));

    const asCategories =
        entity?.rd?.pbn?.isPrivate &&
        entity.contentType == ContentType.BLOG &&
        categoriesList &&
        categoriesList?.length > 0;

    const textValidation = getTextValidation(true, inputValues, docMetrics, entity);

    const handleImageInsertion = (images: IHits[]) => {
        imagesHelperProps.setValue(
            images.map((img, index) => ({ ...img, position: index === 0 ? "title" : null }))
        );

        const commentFromProofreader = commnetFromProofreaderMetaProps.value || "";

        if (
            (!commentFromProofreader.includes("Image changée") &&
                entity?.content?.writingType === WritingType.AI_AUTO,
            entity?.content?.images &&
                entity?.content?.images[0].largeImageUrl !== images[0].largeImageUrl)
        ) {
            commnetFromProofreaderHelperProps.setValue(
                `${commentFromProofreader} Image changée \n`
            );
        }
    };

    useEffect(() => {
        const checked =
            allChecksPassed(
                true,
                currentUser.role,
                inputValues,
                imagesMetaProps.value,
                docMetrics,
                entity,
                true
            ) &&
            rateMetaProps.value != undefined &&
            ((asCategories && categoryMetaProps?.value?.length > 0) || !asCategories);

        if (importantReason || checked) {
            handleDisableButton(false);
        } else {
            handleDisableButton(true);
        }
    }, [textValidation]);

    //we need this code to remove the "autoFocus" from the modal, if we don't have this code we can't write in the Link popUp from  CKEditor
    useEffect(() => {
        const doc = document.querySelector(".modal");
        doc?.removeAttribute("tabindex");
    }, []);

    return (
        <Row className="reviewTextOrderFormWrapper">
            <Col xl="2">
                <div className="d-flex flex-column gap-2 mb-4">
                    <div>
                        <b>
                            {formatMessage({
                                id: `common.field.contentType.${entity?.contentType}`,
                            })}{" "}
                            {(entity?.platform &&
                                !entity?.writerNeeded &&
                                entity?.content?.writingType === "WRITE" && (
                                    <>{formatMessage({ id: "common.field.writerNotNeeded" })}</>
                                )) || (
                                <>
                                    {formatMessage({
                                        id: `common.field.writingType.${entity?.content?.writingType}`,
                                    })}
                                </>
                            )}
                        </b>{" "}
                        {formatMessage({ id: `common.field.of` })}
                    </div>

                    <div className="d-flex gap-2">
                        {formatMessage({ id: "common.field.between" })} <b>{entity?.wordCount} </b>{" "}
                        {formatMessage({ id: "common.field.and" })}{" "}
                        <b>{Math.floor((entity?.wordCount || 0) * 1.6)} </b>
                        {formatMessage({ id: "common.field.words" })} ({textValidation.wordNumber}
                        )
                        <ValidationIconHandler validation={textValidation.wordNumberCheckPass} />
                    </div>
                </div>

                <div>
                    {(entity?.rd && (
                        <div className="d-flex gap-2">
                            {formatMessage({ id: "common.field.for" })} <b>{entity?.rd.domain}</b> (
                            {entity?.rd.thematic.name})
                        </div>
                    )) || (
                        <div className="d-flex gap-2">
                            <b>{formatMessage({ id: "common.field.thematic" })}:</b>{" "}
                            {entity?.thematic.name}
                        </div>
                    )}
                    {(entity?.platform && (
                        <div className="d-flex gap-2">
                            <b>{formatMessage({ id: "common.field.platform" })} :</b>
                            {entity?.platform.name}
                        </div>
                    )) || (
                        <div className="d-flex gap-2">
                            <b style={{ width: "52px" }}>
                                {formatMessage({ id: "common.field.moneySite" })} :{" "}
                            </b>
                            <span>
                                {entity?.moneySite?.domain} ({entity?.moneySite?.pbn.name})
                            </span>
                        </div>
                    )}
                </div>

                <Row>
                    <Col sm="6">
                        {entity?.writerNeeded && entity.content?.writingType === WritingType.WRITE && (
                            <>
                                {textValidation.h2CountNeeded > 0 && (
                                    <div className="d-flex gap-2">
                                        <b>H2:</b>
                                        {textValidation.h2Count} / {textValidation.h2CountNeeded}
                                        <ValidationIconHandler
                                            validation={textValidation.h2CountCheckPass}
                                        />
                                    </div>
                                )}

                                {textValidation.h3CountNeeded > 0 && (
                                    <div className="d-flex gap-2">
                                        <b>H3:</b>
                                        {textValidation.h3Count} / {textValidation.h3CountNeeded}
                                        <ValidationIconHandler
                                            validation={textValidation.h3CountCheckPass}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {textValidation.existInvalidH2 && (
                            <div className="d-flex gap-2">
                                <b> {textValidation.numberOfInvalidH2} ligne vide H2</b>
                                <ValidationIconHandler validation={false} />
                            </div>
                        )}
                        {textValidation.existInvalidH3 && (
                            <div className="d-flex gap-2">
                                <b> {textValidation.numberOfInvalidH3} ligne vide H3</b>
                                <ValidationIconHandler validation={false} />
                            </div>
                        )}
                    </Col>

                    <Col sm="6">
                        {((entity?.writerNeeded &&
                            entity.content?.writingType === WritingType.WRITE) ||
                            (!entity?.writerNeeded &&
                                entity?.content?.writingType === WritingType.AI_AUTO)) && (
                            <>
                                <div className="d-flex gap-2">
                                    <ValidationIconHandler
                                        validation={textValidation.boldCountCheckPass}
                                    />
                                    <b>{formatMessage({ id: "common.field.bold" })}</b>
                                </div>
                                <div className="d-flex gap-2">
                                    <ValidationIconHandler
                                        validation={textValidation.italicCountCheckPass}
                                    />
                                    <b>{formatMessage({ id: "common.field.italic" })}</b>
                                </div>
                            </>
                        )}
                        {((entity?.writerNeeded &&
                            entity.content?.writingType === WritingType.WRITE) ||
                            (!entity?.writerNeeded &&
                                entity?.content?.writingType === WritingType.AI_AUTO)) && (
                            <>
                                {textValidation.listCountNeeded > 0 && (
                                    <div className="d-flex gap-2">
                                        <ValidationIconHandler
                                            validation={textValidation.listCountCheckPass}
                                        />
                                        <b>{formatMessage({ id: "common.field.list" })}</b>
                                    </div>
                                )}
                            </>
                        )}
                    </Col>
                </Row>

                {entity?.links && (
                    <>
                        <div className="mt-5">
                            {formatMessage({ id: "common.field.links" })} :{" "}
                            <b>
                                {formatMessage({ id: "common.field.url" })} (
                                {formatMessage({ id: "common.field.anchor" })})
                            </b>
                            <div className="links">
                                {entity?.links?.map((link, index) => (
                                    <Row
                                        key={index}
                                        className="border-1 link-copyPaste"
                                        onClick={() => copyLinkToClipboard(link.anchor, link.url)}
                                    >
                                        <Col xs="1">
                                            <ValidationIconHandler
                                                validation={(() => {
                                                    if (!textValidation?.linksCount) return false;
                                                    const anchor =
                                                        textValidation?.linksCount[
                                                            link.anchor.toLowerCase()
                                                        ];
                                                    if (anchor?.badLink !== 0) return false;

                                                    const url = textValidation?.linksCount[
                                                        link.anchor.toLowerCase()
                                                    ].urls.find((el) => el.url === link.url);

                                                    if (
                                                        url?.maxNumber === 1 &&
                                                        url?.number === url?.maxNumber
                                                    )
                                                        return true;

                                                    if (
                                                        url &&
                                                        url.maxNumber > 1 &&
                                                        url.number >= index + 1
                                                    ) {
                                                        return true;
                                                    }

                                                    return false;
                                                })()}
                                            />
                                        </Col>
                                        <Col xs="10" style={{ maxWidth: "250px" }}>
                                            <div className="d-flex gap-2">
                                                {link.url} ({link.anchor})
                                            </div>
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {entity?.writerNeeded && entity.content?.writingType === WritingType.WRITE && (
                    <>
                        {entity?.keywords && (
                            <>
                                <div className="gap-2">
                                    <b>{formatMessage({ id: "common.field.opti.yes" })}</b>{" "}
                                    {formatMessage({ id: "common.field.on" })}{" "}
                                    {formatMessage({ id: "common.field.keywords" })} :
                                    {entity?.keywords
                                        ?.sort()
                                        ?.map((keyword: string | undefined, index: number) => (
                                            <Row className="mt-2" key={index}>
                                                <Col xl={10}>
                                                    <div className="thematic-react-tag">
                                                        {keyword}
                                                    </div>
                                                </Col>
                                                <Col xl={2}>
                                                    <div className="d-flex gap-2">
                                                        <ValidationIconHandler
                                                            validation={
                                                                (docMetrics?.keywords[
                                                                    keyword || ""
                                                                ] || 0) >=
                                                                parseInt(
                                                                    process.env
                                                                        .REACT_APP_NUMBER_OF_KEYWORD_PER_TEXT ||
                                                                        "0",
                                                                    10
                                                                )
                                                            }
                                                        />
                                                        ({docMetrics?.keywords[keyword || ""] || 0})
                                                    </div>
                                                </Col>
                                            </Row>
                                        ))}
                                </div>
                            </>
                        )}
                    </>
                )}

                <div className="gap-2 mt-5">
                    <b>{formatMessage({ id: "common.field.instructionProofreader" })} :</b>
                    <ol>
                        {entity?.commentForWriter && entity?.writerNeeded && (
                            <li>{formatMessage({ id: "common.field.instructionCheckBrief" })}</li>
                        )}
                        {!entity?.writerNeeded && entity?.content?.writingType === "AI_AUTO" && (
                            <li>{formatMessage({ id: "common.field.instructionCheckPlan" })}</li>
                        )}
                        <li>{formatMessage({ id: "common.field.instructionFirstRead" })}</li>
                        <li>{formatMessage({ id: "common.field.instructionCheckMistake" })}</li>
                        {entity?.links &&
                            (entity?.writerNeeded ||
                                entity?.content?.writingType === "AI_AUTO") && (
                                <li>
                                    {formatMessage({ id: "common.field.instructionCheckLink" })}
                                </li>
                            )}
                        {entity?.platform && (
                            <li>
                                {formatMessage({ id: "common.field.instructionAuthorityLink" })}
                            </li>
                        )}
                        {(entity?.content?.category && entity?.rd?.categories && (
                            <li>
                                {formatMessage({ id: "common.field.instructionCheckCategory" })}
                            </li>
                        )) || (
                            <li>{formatMessage({ id: "common.field.instructionAddCategory" })}</li>
                        )}

                        {(imagesMetaProps?.value?.length > 0 && (
                            <li>{formatMessage({ id: "common.field.instructionVerifyPhoto" })}</li>
                        )) || <li>{formatMessage({ id: "common.field.instructionAddPhoto" })}</li>}
                        {entity?.writerNeeded && (
                            <li>{formatMessage({ id: "common.field.instructionChoseRate" })}</li>
                        )}
                    </ol>
                </div>

                {entity?.commentForWriter && (
                    <>
                        <div className="gap-2 mt-5">
                            <b>{formatMessage({ id: "common.field.commentForWriter" })} :</b>
                            <div className="comment">{entity?.commentForWriter}</div>
                        </div>
                    </>
                )}

                <div className="mt-5">
                    <b>{formatMessage({ id: "common.field.commentFromProofreader" })}:</b>
                    <SimpleFormTextarea
                        cssClass={["no-resize"]}
                        placeholder={formatMessage({
                            id: "common.field.commentFromProofreader",
                        })}
                        rows={3}
                        maxLength={800}
                        name="commentFromProofreader"
                    />
                </div>
            </Col>

            <Col className="d-flex flex-column" xl={10}>
                <Row>
                    <Col>
                        <SimpleFormControl
                            placeholder={formatMessage({ id: "common.field.title" })}
                            cssClass={["noGrayBackground"]}
                            name="title"
                        />
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "0.1fr 1fr",
                            }}
                        >
                            <div
                                className="d-flex gap-2 form-label"
                                style={{ fontSize: "1.2rem", fontWeight: "500" }}
                            >
                                <div className="d-flex flex-column">
                                    {formatMessage({ id: "common.field.mt" })}{" "}
                                    <div className="d-flex gap-2">
                                        <span style={{ fontSize: "1rem" }}>
                                            ({textValidation.metaTitleCharNumber || "0"} /{" "}
                                            {process.env.REACT_APP_MAX_NUMBER_OF_CHAR_IN_META_TITLE}
                                            )
                                        </span>
                                        <ValidationIconHandler
                                            validation={textValidation.metaTitleCheckPass}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 0.3fr",
                                    columnGap: "8px",
                                }}
                            >
                                <SimpleFormControl
                                    placeholder={formatMessage({
                                        id: "common.field.metaTitle",
                                    })}
                                    cssClass={["noBackground"]}
                                    name="metaTitle"
                                    maxLength={parseInt(
                                        process.env.REACT_APP_MAX_NUMBER_OF_CHAR_IN_META_TITLE ||
                                            "0",
                                        10
                                    )}
                                />

                                {asCategories && categoriesList && (
                                    <SimpleFormSelect
                                        placeholder={formatMessage({
                                            id: "common.field.placeholder.addCategory",
                                        })}
                                        name="category"
                                        values={categoriesList}
                                    />
                                )}
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "0.1fr 1fr" }}>
                            <div
                                className="d-flex gap-2 form-label"
                                style={{ fontSize: "1.2rem", fontWeight: "500" }}
                            >
                                <div className="d-flex flex-column">
                                    {formatMessage({ id: "common.field.md" })}

                                    <>
                                        <div className="d-flex gap-2">
                                            <span style={{ fontSize: "1rem" }}>
                                                ({textValidation.metaDescriptionCharNumber || "0"} /{" "}
                                                {
                                                    process.env
                                                        .REACT_APP_MAX_NUMBER_OF_CHAR_IN_META_DESC
                                                }
                                                )
                                            </span>
                                            <ValidationIconHandler
                                                validation={textValidation.metaDescriptionCheckPass}
                                            />
                                        </div>
                                    </>
                                </div>
                            </div>

                            <div className="flex-grow-1">
                                <SimpleFormTextarea
                                    cssClass={["no-resize"]}
                                    placeholder={formatMessage({
                                        id: "common.field.metaDescription",
                                    })}
                                    rows={2}
                                    maxLength={parseInt(
                                        process.env.REACT_APP_MAX_NUMBER_OF_CHAR_IN_META_DESC ||
                                            "0",
                                        10
                                    )}
                                    name="metaDescription"
                                />
                            </div>
                        </div>
                    </Col>
                    <Col xl={3}>
                        <div
                            className={` ${
                                imagesMetaProps?.value?.length > 0 ? "image-selected" : "d-flex"
                            }`}
                        >
                            {imagesMetaProps?.value && imagesMetaProps?.value?.length > 0 && (
                                <div className="image-gallery">
                                    <div className="image-wrapper d-flex gap-3 align-items-center">
                                        <img
                                            className="firstImage"
                                            src={imagesMetaProps?.value[0]?.largeImageUrl}
                                        />
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="d-flex gap-3">
                                    <SearchImageFormContainer
                                        disabled={!(titleMetaProps?.value?.length > 0)}
                                        entity={entity}
                                        title={titleMetaProps.value}
                                        handleImageInsertion={handleImageInsertion}
                                    />
                                    <ValidationIconHandler
                                        validation={imagesMetaProps?.value?.length > 0}
                                    />
                                </div>
                                {imagesMetaProps.touched && imagesMetaProps.error && (
                                    <div className="fv-plugins-message-container">
                                        <div className="fv-help-block">
                                            <span role="alert">
                                                {imagesMetaProps.error.includes("error.")
                                                    ? formatMessage({ id: imagesMetaProps.error })
                                                    : imagesMetaProps.error}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {imagesMetaProps?.value?.map(
                                (image: IHits | ImageContent, index: number) => (
                                    <React.Fragment key={index}>
                                        {index > 0 && (
                                            <>
                                                <div className="image-gallery">
                                                    <div className="image-wrapper d-flex align-items-center">
                                                        <img
                                                            className={
                                                                index == 0 ? "firstImage" : ""
                                                            }
                                                            src={image.largeImageUrl}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    {headersOptions && index > 0 && (
                                                        <SimpleFormSelect
                                                            values={headersOptions}
                                                            name={`images.${index}.position`}
                                                        />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </React.Fragment>
                                )
                            )}
                        </div>
                    </Col>
                </Row>
                <CkEditorCustom
                    data={textMetaProps.value}
                    onReady={(editor) => {
                        const data = editor.getData();
                        const currentDataHandled = handleData(data, entity);
                        setDocMetrics(currentDataHandled);
                        setHeaderOptions(stringsToOptions(currentDataHandled.headers));
                        textHelperProps.setValue(data);
                    }}
                    onChange={(event, editor) => {
                        const data = editor.getData();
                        const currentDataHandled = handleData(data, entity);
                        setDocMetrics(currentDataHandled);
                        setHeaderOptions(stringsToOptions(currentDataHandled.headers));
                        textHelperProps.setValue(data);
                    }}
                />
            </Col>
        </Row>
    );
};

export default ReviewTextOrderForm;
